import type { Point, SignatureData, Stroke } from "./types";
import { MIN_STEP, normalisePressure, quantise } from "./types";

/**
 * Stroke recorder. Deliberately free of DOM and canvas concerns so the capture
 * rules (pen-lifts, undo/redo, duplicate points) can be unit tested directly.
 *
 * `strokeId` is a monotonic counter that never rewinds on undo, matching the
 * original captures where ids kept climbing across sessions.
 */
export class Recorder {
  private strokes: Stroke[] = [];
  private undone: Stroke[] = [];
  private open: Stroke | null = null;
  private nextId = 1;

  begin(x: number, y: number, pressure: number, isPen: boolean, timestamp: number): void {
    // Starting a fresh stroke forks the timeline; anything undone is no longer reachable.
    this.undone = [];
    const id = this.nextId++;
    this.open = { id, points: [this.point(x, y, pressure, isPen, timestamp, id)] };
    this.strokes.push(this.open);
  }

  /** Returns the segment just committed, or null if the point was a no-op. */
  extend(
    x: number,
    y: number,
    pressure: number,
    isPen: boolean,
    timestamp: number,
  ): { from: Point; to: Point } | null {
    if (!this.open) return null;

    const from = this.open.points[this.open.points.length - 1];

    // iPadOS hands the same pointer samples to more than one move event, so a
    // coalesced batch can overlap the one before it. Replayed literally the nib
    // jumps backwards and re-draws ground it already covered — half of a capture
    // could be these. Integer rounding used to collapse the repeats onto one
    // whole-number coordinate and hide them; nothing but the clock can tell them
    // apart, so a sample no newer than the last one has already been recorded.
    if (timestamp <= from.timestamp) return null;

    const to = this.point(x, y, pressure, isPen, timestamp, this.open.id);

    // Holding the pointer still fires a stream of identical points; they add
    // bytes and nothing else. Measured as a distance rather than an integer
    // match, so a slow passage keeps its detail instead of being thinned out.
    if (Math.hypot(to.x - from.x, to.y - from.y) < MIN_STEP) return null;

    this.open.points.push(to);
    return { from, to };
  }

  end(): void {
    // A tap that never moved leaves a 1-point stroke, which draws nothing but
    // still burns a strokeId and pads the file.
    if (this.open && this.open.points.length < 2) {
      this.strokes.pop();
    }
    this.open = null;
  }

  undo(): void {
    if (this.open) return;
    const last = this.strokes.pop();
    if (last) this.undone.push(last);
  }

  redo(): void {
    if (this.open) return;
    const restored = this.undone.pop();
    if (restored) this.strokes.push(restored);
  }

  clear(): void {
    this.strokes = [];
    this.undone = [];
    this.open = null;
    this.nextId = 1;
  }

  /** Adopt strokes from a loaded file so an existing signature can be replayed and re-exported. */
  load(data: SignatureData): void {
    this.clear();
    this.strokes = data.strokes.map((s) => ({ id: s.id, points: [...s.points] }));
    this.nextId = Math.max(0, ...this.strokes.map((s) => s.id)) + 1;
  }

  get isDrawing(): boolean {
    return this.open !== null;
  }

  get canUndo(): boolean {
    return !this.open && this.strokes.length > 0;
  }

  get canRedo(): boolean {
    return !this.open && this.undone.length > 0;
  }

  get isEmpty(): boolean {
    return this.strokes.length === 0;
  }

  all(): Stroke[] {
    return this.strokes;
  }

  points(): Point[] {
    return this.strokes.flatMap((s) => s.points);
  }

  /** Wall-clock span of the signature, in milliseconds. */
  duration(): number {
    const pts = this.points();
    if (pts.length === 0) return 0;
    const ts = pts.map((p) => p.timestamp);
    return Math.max(...ts) - Math.min(...ts);
  }

  /** Throws if empty — callers gate on `isEmpty`, so reaching here with nothing is a bug. */
  toData(): SignatureData {
    const pts = this.points();
    if (pts.length === 0) throw new Error("Nothing to export");
    const ts = pts.map((p) => p.timestamp);
    return {
      strokes: this.strokes,
      startTime: Math.min(...ts),
      endTime: Math.max(...ts),
    };
  }

  private point(
    x: number,
    y: number,
    pressure: number,
    isPen: boolean,
    timestamp: number,
    strokeId: number,
  ): Point {
    return {
      // Kept fractional. Integers were the fidelity ceiling, not a requirement of
      // the format — see PRECISION in types.ts.
      x: quantise(x),
      y: quantise(y),
      timestamp,
      pressure: normalisePressure(pressure, isPen),
      strokeId,
    };
  }
}
